import type { AgentCapability, NodeStatus, ResourcePath } from '@occ/contracts';
import { useCan, type CanReturnType } from '@refinedev/core';
import { useEffect, useRef, useState } from 'react';
import { requiredCapability } from '../auth/rbac';
import { ErrorNotice } from '../components/ErrorNotice';
import { Button } from '../components/ui';
import { fetchNodeDetail } from '../data/nodes';
import { openScreenSession } from '../data/screen';

/**
 * Popover de acciones rápidas del nodo hoja (sección 8.2 del spec).
 *
 * "Ver pantalla" y "Terminal" reusan tal cual la lógica de habilitación de
 * `NodeActions` (sin permiso, capacidad no declarada, nodo desconectado): las
 * capacidades no viajan en el árbol de `HierarchyProvider`, así que se piden
 * una vez al abrir, igual que hace `NodeDetailPage`. "Ver ficha completa" es la
 * vía de escape y no se apaga nunca.
 */
export type LeafActionsPopoverData = {
  nodeId: string;
  alias: string;
  status: NodeStatus;
  path: ResourcePath;
};

type ScreenState =
  | { phase: 'idle' }
  | { phase: 'running' }
  | { phase: 'done'; launchUrl: string; blocked: boolean }
  | { phase: 'failed'; error: unknown };

export function LeafActionsPopover({
  data,
  x,
  y,
  onClose,
  onNavigate,
}: {
  data: LeafActionsPopoverData;
  x: number;
  y: number;
  onClose: () => void;
  onNavigate: (to: string) => void;
}) {
  const shellRef = useRef<HTMLDivElement>(null);
  const online = data.status === 'online';
  const [capabilities, setCapabilities] = useState<AgentCapability[]>([]);
  const [screen, setScreen] = useState<ScreenState>({ phase: 'idle' });

  useEffect(() => {
    let cancelled = false;
    fetchNodeDetail(data.nodeId)
      .then((detail) => {
        if (!cancelled) setCapabilities(detail.capabilities);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [data.nodeId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (shellRef.current && !shellRef.current.contains(event.target as Node)) onClose();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [onClose]);

  const { data: screenPermission } = useCan({
    resource: 'actions',
    action: 'screen.open',
    params: { path: data.path },
  });
  const { data: terminalPermission } = useCan({
    resource: 'actions',
    action: 'terminal.open',
    params: { path: data.path },
  });

  const reasonFor = (permission: CanReturnType | undefined, capability: AgentCapability | null) => {
    if (!permission?.can) return permission?.reason ?? 'Sin permiso';
    if (capability && capabilities.length > 0 && !capabilities.includes(capability)) {
      return `El agente de este nodo no declara la capacidad «${capability}»`;
    }
    if (!online) return 'El nodo no está conectado';
    return null;
  };

  const screenReason = reasonFor(screenPermission, requiredCapability('screen.open'));
  const terminalReason = reasonFor(terminalPermission, requiredCapability('terminal.open'));

  const runScreenOpen = async () => {
    setScreen({ phase: 'running' });
    try {
      const handle = await openScreenSession(data.nodeId);
      setScreen({ phase: 'done', launchUrl: handle.launchUrl, blocked: handle.blocked });
    } catch (error) {
      setScreen({ phase: 'failed', error });
    }
  };

  return (
    <div
      ref={shellRef}
      className="absolute z-10 grid w-56 gap-2 rounded-lg border border-black/15 bg-[var(--occ-surface)] p-3 text-xs shadow-lg dark:border-white/20"
      style={{ left: x, top: y, transform: 'translate(-50%, 8px)' }}
    >
      <div className="font-semibold tracking-tight">{data.alias}</div>

      <div className="grid gap-1.5">
        <Button
          variant="secondary"
          className="w-full justify-start"
          disabled={screenReason !== null || screen.phase === 'running'}
          title={screenReason ?? 'Abre una sesión de pantalla remota (RustDesk)'}
          onClick={() => void runScreenOpen()}
        >
          {screen.phase === 'running' ? 'Abriendo…' : 'Ver pantalla'}
        </Button>
        <Button
          variant="secondary"
          className="w-full justify-start"
          disabled={terminalReason !== null}
          title={terminalReason ?? 'Abre la terminal en la ficha del nodo'}
          onClick={() => onNavigate(`/nodes/${data.nodeId}?tab=terminal`)}
        >
          Terminal
        </Button>
        <Button
          variant="secondary"
          className="w-full justify-start"
          title="Inventario, scripts, servicios e histórico de salud"
          onClick={() => onNavigate(`/nodes/${data.nodeId}`)}
        >
          Ver ficha completa
        </Button>
      </div>

      {screen.phase === 'done' && screen.blocked && (
        <p className="text-[11px] opacity-70">
          Ventana bloqueada:{' '}
          <a className="underline" href={screen.launchUrl} target="_blank" rel="opener">
            abrir
          </a>
        </p>
      )}
      {screen.phase === 'done' && !screen.blocked && (
        <p className="text-[11px] opacity-70">Sesión abierta en otra pestaña.</p>
      )}
      {screen.phase === 'failed' && <ErrorNotice error={screen.error} what="Ver pantalla" />}
    </div>
  );
}
